import { ref } from 'vue'

/**
 * 营销政策 composable（H5版）
 * @param {Ref} form - 订单表单
 * @param {Ref} marketingData - 营销活动数据
 * @param {Function} refreshAmount - 金额刷新回调
 * @param {Ref} specialDiscountReviewerList - 特批审批人列表
 * @param {Map} maxReductionAmount - 各授课类型最大减免金额
 * @param {Map} maxGiftHour - 各授课类型最大赠送课时
 */
export function useMarketing(
  form,
  marketingData,
  refreshAmount,
  specialDiscountReviewerList,
  maxReductionAmount,
  maxGiftHour
) {
  const discountAvailableMarketing = ref([])
  const reductionAvailableMarketing = ref([])
  const giftAvailableMarketing = ref([])
  const editDiscount = ref(false)

  /** 获取不同授课类型的总课时数据 */
  const getClassHourTotalByTeachingModel = () => {
    const classHourTotalByTeachingModel = { 1: 0, 2: 0, 3: 0 }
    form.value.studyCourseList?.forEach((course) => {
      const type = course.teachingType || 1
      classHourTotalByTeachingModel[type] += parseFloat(course.buyClassHourCount) || 0
    })
    return classHourTotalByTeachingModel
  }

  /** 初始化营销活动数据 */
  const initAvailableMarketing = () => {
    discountAvailableMarketing.value = []
    reductionAvailableMarketing.value = []
    giftAvailableMarketing.value = []
    const classHourTotalByTeachingModel = getClassHourTotalByTeachingModel()
    if (Object.values(classHourTotalByTeachingModel).some(total => total > 0)) {
      for (const activityType in marketingData.value) {
        const items = marketingData.value[activityType]
        items?.forEach((item) => {
          const { satisfy, teachingType } = item
          const classHourTotalForModel = classHourTotalByTeachingModel[teachingType] || 0
          if (parseFloat(classHourTotalForModel) >= parseFloat(satisfy)) {
            switch (parseInt(activityType)) {
              case 1:
                discountAvailableMarketing.value.push(item)
                break
              case 2:
                reductionAvailableMarketing.value.push(item)
                break
              case 3:
                giftAvailableMarketing.value.push(item)
                break
            }
          }
        })
      }
    }
  }

  /** 重置营销政策 */
  const resetMarketing = () => {
    discountAvailableMarketing.value = []
    reductionAvailableMarketing.value = []
    giftAvailableMarketing.value = []
    form.value.selectDiscountCard = []
    form.value.selectReductionCard = []
    form.value.selectGiftCard = []
    refreshAmount()
  }

  /** 清空减免金额 */
  const clearReductionAmount = (teachingType) => {
    form.value.studyCourseList?.forEach((studyCourse) => {
      if (!teachingType || teachingType === studyCourse.teachingType) {
        studyCourse.reductionAmount = 0
      }
    })
  }

  /** 清空赠送课时 */
  const clearGiftClassHourCount = (teachingType) => {
    form.value.studyCourseList?.forEach((studyCourse) => {
      if (!teachingType || teachingType === studyCourse.teachingType) {
        studyCourse.giftClassHourCount = 0
      }
    })
  }

  /** 刷新可用营销活动 */
  const refreshAvailableMarketing = () => {
    if (form.value.isSpecialDiscount) return
    clearGiftClassHourCount(false)
    clearReductionAmount(false)
    resetMarketing()
    initAvailableMarketing()
  }

  /** 重置赠送课时及扣减金额 */
  const resetGiftAndDiscount = () => {
    form.value.studyCourseList?.forEach((studyCourse) => {
      studyCourse.giftClassHourCount = 0
      studyCourse.reductionAmount = 0
      studyCourse.discount = 100
    })
  }

  /** 修改课程优惠及折扣可改 */
  const setEditReductionAmountAndGiftClassHourCount = (flag) => {
    form.value.studyCourseList?.forEach((row) => {
      row.editReductionAmount = flag
      row.editGiftClassHourCount = flag
    })
  }

  /** 折扣卡片选中事件 */
  const handleDiscountCardSelect = (discountRecord) => {
    const index = form.value.selectDiscountCard?.findIndex(
      (record) => record.settingId === discountRecord.settingId
    )
    if (index === -1) {
      const existingIndex = form.value.selectDiscountCard?.findIndex(
        (record) => record.teachingType === discountRecord.teachingType
      )
      if (existingIndex !== undefined && existingIndex !== -1) {
        form.value.selectDiscountCard?.splice(existingIndex, 1)
      }
      form.value.selectDiscountCard?.push(discountRecord)
      form.value.studyCourseList?.forEach((row) => {
        if (row.teachingType === discountRecord.teachingType) {
          row.discount = discountRecord.gift * 10
        }
      })
    } else {
      form.value.selectDiscountCard?.splice(index, 1)
      form.value.studyCourseList?.forEach((row) => {
        if (row.teachingType === discountRecord.teachingType) {
          row.discount = 100
        }
      })
    }
    refreshAmount()
  }

  /** 减免卡片选中事件 */
  const handleReductionCardSelect = (discountRecord) => {
    clearReductionAmount(discountRecord.teachingType)
    const index = form.value.selectReductionCard?.findIndex(
      (record) => record.settingId === discountRecord.settingId
    )
    if (index === -1) {
      const existingIndex = form.value.selectReductionCard?.findIndex(
        (record) => record.teachingType === discountRecord.teachingType
      )
      if (existingIndex !== undefined && existingIndex !== -1) {
        form.value.selectReductionCard?.splice(existingIndex, 1)
      }
      form.value.selectReductionCard?.push(discountRecord)
      form.value.studyCourseList?.forEach((row) => {
        if (row.teachingType === discountRecord.teachingType) {
          row.editReductionAmount = true
        }
      })
      maxReductionAmount.set(discountRecord.teachingType, discountRecord.gift)
    } else {
      form.value.selectReductionCard?.splice(index, 1)
      form.value.studyCourseList?.forEach((row) => {
        if (row.teachingType === discountRecord.teachingType) {
          row.editReductionAmount = false
        }
      })
      maxReductionAmount.delete(discountRecord.teachingType)
    }
    refreshAmount()
  }

  /** 赠送课时卡片选中事件 */
  const handleGiftCardSelect = (discountRecord) => {
    clearGiftClassHourCount(discountRecord.teachingType)
    const index = form.value.selectGiftCard?.findIndex(
      (record) => record.settingId === discountRecord.settingId
    )
    if (index === -1) {
      const existingIndex = form.value.selectGiftCard?.findIndex(
        (record) => record.teachingType === discountRecord.teachingType
      )
      if (existingIndex !== undefined && existingIndex !== -1) {
        form.value.selectGiftCard?.splice(existingIndex, 1)
      }
      form.value.selectGiftCard?.push(discountRecord)
      form.value.studyCourseList?.forEach((row) => {
        if (row.teachingType === discountRecord.teachingType) {
          row.editGiftClassHourCount = true
        }
      })
      maxGiftHour.set(discountRecord.teachingType, discountRecord.gift)
    } else {
      form.value.selectGiftCard?.splice(index, 1)
      form.value.studyCourseList?.forEach((row) => {
        if (row.teachingType === discountRecord.teachingType) {
          row.editGiftClassHourCount = false
        }
      })
      maxGiftHour.delete(discountRecord.teachingType)
    }
  }

  /** 初始化活动选中卡片 */
  const initSelectCards = (orderMarketingMap) => {
    form.value.selectDiscountCard = []
    form.value.selectReductionCard = []
    form.value.selectGiftCard = []
    if (!orderMarketingMap) return
    for (const activityType in orderMarketingMap) {
      const type = Number(activityType)
      const items = orderMarketingMap[type]
      if (!items) continue
      if (type === 1) {
        form.value.selectDiscountCard?.push(...items)
      }
      if (type === 2) {
        items.forEach((item) => {
          maxReductionAmount.set(item.teachingType, item.gift)
        })
        form.value.selectReductionCard?.push(...items)
      }
      if (type === 3) {
        items.forEach((item) => {
          maxGiftHour.set(item.teachingType, item.gift)
        })
        form.value.selectGiftCard?.push(...items)
      }
      initEditReductionAmountAndGiftClassHourCount(items)
    }
  }

  /** 初始化修改项 */
  const initEditReductionAmountAndGiftClassHourCount = (items) => {
    items?.forEach((item) => {
      if (item.activityType === 2) {
        form.value.studyCourseList?.forEach((row) => {
          if (row.teachingType === item.teachingType) {
            row.editReductionAmount = true
          }
        })
      }
      if (item.activityType === 3) {
        form.value.studyCourseList?.forEach((row) => {
          if (row.teachingType === item.teachingType) {
            row.editGiftClassHourCount = true
          }
        })
      }
    })
  }

  /** 处理特批优惠 */
  const handleSpecialDiscount = () => {
    form.value.specialDiscount = 0
    if (form.value.isSpecialDiscount) {
      setEditReductionAmountAndGiftClassHourCount(true)
      editDiscount.value = true
      resetMarketing()
      form.value.reviewer = specialDiscountReviewerList.value[0]?.userId
    } else {
      setEditReductionAmountAndGiftClassHourCount(false)
      form.value.reductionAmount = undefined
      resetGiftAndDiscount()
      refreshAvailableMarketing()
      editDiscount.value = false
      form.value.reviewer = undefined
    }
  }

  /** 判断卡片是否选中 */
  const isCheckedCards = (selectCards, card) => {
    if (!selectCards) return false
    return selectCards.some((selectCard) => selectCard.settingId === card.settingId)
  }

  return {
    discountAvailableMarketing,
    reductionAvailableMarketing,
    giftAvailableMarketing,
    editDiscount,
    getClassHourTotalByTeachingModel,
    initAvailableMarketing,
    refreshAvailableMarketing,
    clearReductionAmount,
    clearGiftClassHourCount,
    resetMarketing,
    resetGiftAndDiscount,
    setEditReductionAmountAndGiftClassHourCount,
    handleDiscountCardSelect,
    handleReductionCardSelect,
    handleGiftCardSelect,
    initSelectCards,
    initEditReductionAmountAndGiftClassHourCount,
    handleSpecialDiscount,
    isCheckedCards
  }
}
